import * as React from 'react';
import Link from 'next/link';
import { withStyles } from '@material-ui/core/styles';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import scrollIntoView from 'scroll-into-view';

export interface Props {
  homepage?: boolean;
  classes: any;
}

export interface State {
  expanded: string;
}

const styles = {
  content: {
    fontSize: 20,
    fontWeight: 300,
  },
  expanded: {
    margin: '20px auto 0 !important',
  },
  heading: {
    fontSize: 20,
    fontWeight: 500,
  },
  root: {
    margin: '30px auto 0',
    maxWidth: 910,
    width: '100%',
  },
  summaryRoot: {
    margin: '0 auto',
  },
};

const questionsContent = [
  {
    answer: (
      <span>
        Torii is the easiest way to buy a home. When you buy a home with Torii,
        you receive an agent, attorney, appraisal, lender, and more all in one
        place. The entire transaction is done through our app, where you can
        search for properties, manage showings, make an offer, and track your
        progress every step of the way. We automate many of the tedious and
        time-consuming aspects of the home buying process so that you can focus
        on the things that really matter.
      </span>
    ),
    homepage: false,
    id: 1,
    question: 'What is Torii?',
  },
  {
    answer: (
      <span>
        We currently support all of Massachusetts. If you&#39;re interested in
        using Torii in your city,{' '}
        <Link href="/contact">
          <a>let us know</a>
        </Link>
        .
      </span>
    ),
    homepage: true,
    id: 2,
    question: 'Where can I use Torii?',
  },
  {
    answer: (
      <span>
        Closing costs are extra fees on top of the price of the home that you
        have to pay to complete the sale. These can include loan origination
        fees, appraisal fees, title search, surveys, deed-recording fees, credit
        report charges, and more. To see a full breakdown of each closing
        cost/fee, try our{' '}
        <Link href="/tools/closing-costs">
          <a>Closing Cost Calculator</a>
        </Link>
        .
      </span>
    ),
    homepage: true,
    id: 3,
    question: 'What are closing costs?',
  },
  {
    answer: (
      <span>
        There&#39;s no catch. We understand that you may have initial doubts, so
        please{' '}
        <Link href="/reviews">
          <a target="_blank">read through our client reviews</a>
        </Link>{' '}
        to see what people using Torii really think.
      </span>
    ),
    homepage: false,
    id: 4,
    question: "What's the catch?",
  },
  {
    answer: (
      <span>
        Yes, as long as the property is on the market in Massachusetts.
      </span>
    ),
    homepage: true,
    id: 5,
    question: 'Does Torii work with any home?',
  },
  {
    answer: (
      <span>
        Yes, Torii provides you with an expert licensed expert real estate agent
        to work with you throughout every step of the home buying process. This
        will include help with your home search, scheduling/attending showings
        with you, as well as preparing your offer and price negotiation when the
        time comes. And, in addition to your dedicated agent, you&#39;ll have
        the entire Torii Team working behind the scenes to ensure your home
        buying process is smooth, enjoyable, and successful.
      </span>
    ),
    homepage: true,
    id: 6,
    question: 'Does Torii act as my real estate agent?',
  },
  {
    answer: (
      <span>
        Once your offer is accepted, we connect you with all the necessary
        providers and services such as a real estate attorney, insurance agent,
        appraisal, and inspection company.
      </span>
    ),
    id: 7,
    homepage: false,
    question: 'What services does Torii provide?',
  },
  {
    answer: (
      <span>
        Yes! We don&#39;t provide loans, but we work with several great lenders
        and mortgage brokers that we are happy to put you in touch with to begin
        the pre-approval process.{' '}
        <Link href="/get-preapproved">
          <a>Click here for an introduction to a lender.</a>
        </Link>
      </span>
    ),
    homepage: false,
    id: 8,
    question: 'Can Torii help me get a mortgage?',
  },
  {
    answer: (
      <span>
        We do not support buying foreclosures or auction properties at this
        time. If you have questions, contact us.
      </span>
    ),
    homepage: false,
    id: 9,
    question: 'Can I buy a foreclosure or auction property with Torii?',
  },
];

export class Faq extends React.Component<Props, State> {
  itemRefs: any = {};
  constructor(props: any) {
    super(props);
    this.state = {
      expanded: '',
    };
  }

  findQuestionId = (location: string) => {
    const id = location.split('#')[1];
    if (id && id.length > 0) {
      return parseInt(id, 10);
    } else {
      return false;
    }
  };

  componentDidMount() {
    const location = window.location.href;
    if (location.indexOf('/buying-with-torii#') >= 0) {
      const questionId = this.findQuestionId(location);
      if (questionId) {
        this.setState(
          {
            expanded: `panel${questionId}`,
          },
          () => {
            scrollIntoView(this.itemRefs[questionId], {
              align: { top: 1, topOffset: -310 },
              time: 500,
            });
          },
        );
      }
    }
  }

  handleChange = (panel: any) => {
    return (event: any, expanded: any) => {
      this.setState({
        expanded: expanded ? panel : false,
      });
    };
  };

  render() {
    const { classes, homepage } = this.props;
    const { expanded } = this.state;
    let questionsToUse = questionsContent;
    if (homepage) {
      questionsToUse = [];
      for (let i = 0; i < questionsContent.length; i += 1) {
        if (questionsContent[i].homepage) {
          questionsToUse.push(questionsContent[i]);
        }
      }
      // Sorting the questions based on id
      questionsToUse = questionsToUse.sort((a, b) => {
        return a.id - b.id;
      });
    }

    const questions = questionsToUse.map((item, index) => (
      <Accordion
        ref={el => (this.itemRefs[item.id] = el)}
        key={item.id}
        id={`${item.id}`}
        expanded={expanded === `panel${index + 1}`}
        onChange={this.handleChange(`panel${index + 1}`)}
        style={{ paddingTop: 25 }}
        elevation={0}
      >
        <AccordionSummary
          classes={{ root: classes.summaryRoot, expanded: classes.expanded }}
          expandIcon={<ExpandMore />}
        >
          <Typography className={classes.heading}>{item.question}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" className={classes.content}>
            {item.answer}
          </Typography>
        </AccordionDetails>
      </Accordion>
    ));

    return <div className={classes.root}>{questions}</div>;
  }
}

export default withStyles(styles)(Faq);
